import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useStateControllerStore,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  GDSHeadingLevel,
  HeadingText,
  Image,
  StackLayout,
} from '@leagueplatform/genesis-core';

export const NODE_TYPE_ORDER_NEW_CARD_MEMBER_SUMMARY_CARD =
  'orderNewCardMemberSummaryCard' as const;

type OrderNewCardMemberSummaryCardProps = {
  image?: {
    url: string;
    description?: string;
    width?: string;
    height?: string;
  };
  heading: {
    heading: string;
    headingLevel?: GDSHeadingLevel;
  };
  formId: string;
  inputIds: string[];
  memberList: { label: string; value: string }[];
};

export type OrderNewCardMemberSummaryCardNodeActions = {
  onLoad?: MasonryEngineNodeAction;
};

export type OrderNewCardMemberSummaryCardNode = MasonryEngineNode<
  typeof NODE_TYPE_ORDER_NEW_CARD_MEMBER_SUMMARY_CARD,
  OrderNewCardMemberSummaryCardProps,
  OrderNewCardMemberSummaryCardNodeActions
>;

export type OrderNewCardMemberSummaryCardRendererProps =
  MasonryEngineNodeRendererProps<OrderNewCardMemberSummaryCardNode>;

const formatClassNames = (...classNames: (string | undefined)[]) =>
  classNames?.filter(Boolean).join(' ') || '';

const normalizeToLines = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value
      .flatMap((v) => normalizeToLines(v))
      .map((v) => v.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const str = value.trim();
    return str ? [str] : [];
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? [String(value)] : [];
  }

  // Ignore unsupported types (e.g. objects/booleans) to avoid rendering
  // unhelpful strings like "[object Object]".
  return [];
};

/**
 * Masonry renderer for `orderNewCardMemberSummaryCard`.
 *
 * - **Source of truth**: reads the current form field values from the Masonry state store using `formId`.
 * - **Selection**: only values for the provided `inputIds` are considered (in order).
 * - **Display**: each selected value is mapped through `memberList` (matching on `memberList[].value`);
 *   when a match exists, the corresponding `memberList[].label` is rendered.
 */
export const OrderNewCardMemberSummaryCardRenderer = ({
  image,
  heading,
  formId,
  inputIds,
  memberList,
  rootElementRef,
  engineClassName,
  className,
  locatorId,
}: OrderNewCardMemberSummaryCardRendererProps) => {
  const resolveLabel = React.useCallback(
    (value: string) => memberList.find((m) => m.value === value)?.label,
    [memberList],
  );

  const lineItems = useStateControllerStore((store) => {
    const fieldNodeIds = store.getFormFieldNodesMap(formId) || [];

    const valuesByFormFieldId = new Map<string, unknown>();

    fieldNodeIds.forEach((fieldNodeId) => {
      const nodeState = store.getNodeState<any>(fieldNodeId);
      const formFieldId = nodeState?.formFieldProperties?.formFieldId;
      if (typeof formFieldId === 'string') {
        valuesByFormFieldId.set(formFieldId, nodeState?.value);
      }
    });

    return inputIds.flatMap((inputId) => {
      const lines = normalizeToLines(valuesByFormFieldId.get(inputId));
      return lines.flatMap((line, lineIndex) => {
        const text = resolveLabel(line);
        if (!text) return [];

        return [
          {
            // Avoid using array indices directly as React keys; instead compute a stable key string.
            // Note: using lineIndex here is safe because it is part of a derived key, not a raw key prop.
            key: `${inputId}:${lineIndex}:${line}`,
            text,
          },
        ];
      });
    });
  });

  return (
    <Box
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={{
        borderStyle: 'solid',
        borderColor: '$onSurfaceBorderSubdued',
        borderRadius: '$card',
        borderWidth: '$thin',
        padding: '$one',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '$half',
      }}
    >
      {image?.url ? (
        <Image
          src={image.url}
          alt={image.description ?? ''}
          width={image.width ?? '48'}
          height={image.height ?? '48'}
        />
      ) : null}

      <StackLayout spacing="$none">
        <HeadingText level={heading.headingLevel ?? '4'} size="sm">
          {heading.heading}
        </HeadingText>

        {lineItems.length > 0 && (
          <Box
            as="ul"
            css={{
              margin: 0,
            }}
          >
            {lineItems.map((item) => (
              <Box
                as="li"
                key={item.key}
                css={{
                  margin: 0,
                  padding: 0,
                  typography: '$bodyTwo',
                  listStyleType: 'none',
                }}
              >
                {item.text}
              </Box>
            ))}
          </Box>
        )}
      </StackLayout>
    </Box>
  );
};
